"use client";

import {
  loginWithProviderToken,
  sessionLogin,
  logout,
  loginWithBasicAuth, // <-- NOVA IMPORTAÇÃO
} from "@/app/_next/actions/auth.action";
import useCaseFactory from "@/app/_useCases/UseCaseFactory";
import { IRouteAuthLogin } from "@/types/src/backend/routes/auth/login";
import { ContentToken } from "@/types/src/backend/utils/jwt";
import { usePathname, useRouter } from "next/navigation";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import Spinner from "../Spinner";
import Link from "next/link";

const UserContext = createContext<{
  user: ContentToken | null;
  loginWithProvider: (
    type: IRouteAuthLogin["body"]["type"],
    providerToken: string
  ) => Promise<void>;
  // NOVA FUNÇÃO EXPOSTA
  loginWithBasic: (email: string, password: string) => Promise<string | void>;
  loading: boolean;
  logout: () => Promise<void>;
}>({
  user: null,
  loginWithProvider: async () => {},
  loginWithBasic: async () => {}, // Mock inicial
  loading: true,
  logout: async () => {},
});

export default function SessionWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();

  const [user, setUser] = useState<ContentToken | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loginWithProvider = useCallback(
    async (type: IRouteAuthLogin["body"]["type"], providerToken: string) => {
      setIsLoading(true);
      const mdcAuth = await loginWithProviderToken(type, providerToken);
      if (mdcAuth.error) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      setUser(mdcAuth.userData);
      setIsLoading(false);
      router.push(mdcAuth.redirectTo);
    },
    [router]
  );

  // NOVA LÓGICA DE LOGIN BÁSICO NO CONTEXTO
  const loginWithBasic = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      const mdcAuth = await loginWithBasicAuth(email, password);
      
      if (mdcAuth.error) {
        setUser(null);
        setIsLoading(false);
        return mdcAuth.error; // Retorna o erro pro Form exibir
      }

      setUser(mdcAuth.userData);
      setIsLoading(false);
      router.push(mdcAuth.redirectTo);
    },
    [router]
  );

  const endSession = useCallback(async () => {
    setIsLoading(true);

    try {
      const authenticatorUsecase =
        useCaseFactory.makeAzureAuhenticatorUseCase();

      await authenticatorUsecase.logout();
      await logout();
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  // Silent login
  useEffect(() => {
    async function silentLogin() {
      let user: ContentToken | null = null;
      setIsLoading(true);
      try {
        user = await sessionLogin();
      } catch (error) {
        user = null;
      } finally {
        setIsLoading(false);
        setUser(user);
        if (!user) {
          router.push("/login");
        } else {
          if (pathname === "/login") {
            router.push("/");
          }
        }
      }
    }

    silentLogin();
  }, [router, pathname]);

  useEffect(() => {
    if (user && pathname === "/login") {
      router.push("/");
    }

    if ((user === null || user === undefined) && !isLoading) {
      router.push("/login");
    }
  }, [user, isLoading, pathname, router]);

  if (!user) {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full text-c-text">
          <p className="mb-4">Carregando...</p>
          <Spinner />
        </div>
      );
    }

    if (pathname !== "/login") {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <p>Você não está logado. Por favor, faça login para continuar.</p>
          <Link
            href="/login"
            className="text-c-text p-4 bg-c-primary font-bold rounded"
          >
            Ir para Login
          </Link>
        </div>
      );
    }
  }

  return (
    <UserContext.Provider
      value={{
        user,
        loginWithProvider,
        loginWithBasic, // Expõe no provider
        loading: isLoading,
        logout: endSession,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useAuth() {
  const { user, loginWithProvider, loginWithBasic, loading, logout } = useContext(UserContext);

  return {
    user: user!,
    loginWithProvider,
    loginWithBasic, // Expõe no hook
    loading,
    logout,
  };
}